import type { ArenaLayout } from './layout-types';
import { ROUTES_DEEP } from './routes-deep';

/**
 * ADVANCE routes (docs/01 advance rooms): fight segments joined by gated
 * doors, ending in a boss chamber sized for the boss. Segment obstacles and
 * turrets are fractions of that segment's outer rect (x → width, y → height;
 * a vertical segment is `span` wide and `len` tall). The ship enters segment
 * 1 at the end opposite its exit. Geometry is harness-checked (no overlaps,
 * clear doorways, every spawn off rocks/pits).
 */
/** S1 Scrap Belt + S2 Elemental Core routes. */
const ROUTES_SHALLOW: ReadonlyArray<ArenaLayout> = [
  {
    // S1: a straight freight spine — learn the gates
    id: 'scrap-spine',
    nameKey: 'arena.scrap-spine',
    obstacles: [],
    route: {
      segments: [
        {
          exit: 'up', len: 2300, span: 1400,
          obstacles: [{ x: 0.27, y: 0.42, radius: 60 }, { x: 0.74, y: 0.3, radius: 55 }],
        },
        {
          exit: 'up', len: 2400, span: 1400,
          obstacles: [
            { x: 0.5, y: 0.52, radius: 70 },
            { x: 0.18, y: 0.26, radius: 62, lava: true },
            { x: 0.82, y: 0.7, radius: 62, lava: true },
          ],
          turrets: [{ x: 0.17, y: 0.12 }, { x: 0.83, y: 0.12 }],
        },
      ],
      chamber: { obstacles: [{ x: 0.2, y: 0.22, radius: 60 }, { x: 0.8, y: 0.78, radius: 60 }] },
    },
  },
  {
    // S1: a dock dogleg — up, then a long run east into the chamber
    id: 'scrap-dogleg',
    nameKey: 'arena.scrap-dogleg',
    obstacles: [],
    route: {
      segments: [
        {
          exit: 'up', len: 2200, span: 1400,
          obstacles: [{ x: 0.3, y: 0.35, radius: 58 }, { x: 0.7, y: 0.58, radius: 58 }],
        },
        {
          exit: 'right', len: 2700, span: 1400, entryAt: 0.2,
          obstacles: [
            { x: 0.45, y: 0.3, radius: 65 },
            { x: 0.62, y: 0.72, radius: 65 },
            { x: 0.82, y: 0.45, radius: 60, lava: true },
          ],
        },
      ],
      chamber: { obstacles: [{ x: 0.3, y: 0.24, radius: 60 }, { x: 0.3, y: 0.76, radius: 60 }] },
    },
  },
  {
    // S2: a magma conduit — three halls east, lava creeping in
    id: 'core-conduit',
    nameKey: 'arena.core-conduit',
    obstacles: [],
    route: {
      segments: [
        {
          exit: 'right', len: 2300, span: 1350,
          obstacles: [{ x: 0.5, y: 0.22, radius: 70, lava: true }, { x: 0.62, y: 0.74, radius: 60 }],
        },
        {
          exit: 'right', len: 2300, span: 1450,
          obstacles: [
            { x: 0.3, y: 0.8, radius: 80, lava: true },
            { x: 0.62, y: 0.26, radius: 80, lava: true },
            { x: 0.5, y: 0.5, radius: 58 },
          ],
        },
        {
          exit: 'right', len: 2200, span: 1350,
          obstacles: [{ x: 0.4, y: 0.25, radius: 62 }, { x: 0.65, y: 0.72, radius: 62 }],
          turrets: [{ x: 0.86, y: 0.16 }, { x: 0.86, y: 0.84 }],
        },
      ],
      chamber: {
        obstacles: [{ x: 0.5, y: 0.14, radius: 70, lava: true }, { x: 0.5, y: 0.86, radius: 70, lava: true }],
      },
    },
  },
  {
    // S2: a vent switchback — up, back west, up again
    id: 'core-switchback',
    nameKey: 'arena.core-switchback',
    obstacles: [],
    route: {
      segments: [
        {
          exit: 'up', len: 2200, span: 1400,
          obstacles: [{ x: 0.26, y: 0.4, radius: 60 }, { x: 0.76, y: 0.24, radius: 70, lava: true }],
        },
        {
          exit: 'left', len: 2600, span: 1350, entryAt: 0.8,
          obstacles: [
            { x: 0.5, y: 0.3, radius: 75, lava: true },
            { x: 0.36, y: 0.72, radius: 60 },
            { x: 0.6, y: 0.66, radius: 55 },
          ],
        },
        {
          exit: 'up', len: 2200, span: 1400, entryAt: 0.8,
          obstacles: [{ x: 0.3, y: 0.42, radius: 62 }, { x: 0.72, y: 0.3, radius: 62 }],
          turrets: [{ x: 0.5, y: 0.14 }],
        },
      ],
      chamber: { obstacles: [{ x: 0.18, y: 0.5, radius: 70, lava: true }] },
    },
  },
  {
    // S2: a crucible run — east, a drop south, east again into the heat
    id: 'core-crucible',
    nameKey: 'arena.core-crucible',
    obstacles: [],
    route: {
      segments: [
        {
          exit: 'right', len: 2300, span: 1400,
          obstacles: [{ x: 0.46, y: 0.24, radius: 62 }, { x: 0.7, y: 0.8, radius: 70, lava: true }],
        },
        {
          exit: 'down', len: 2400, span: 1400, entryAt: 0.2,
          obstacles: [
            { x: 0.72, y: 0.36, radius: 78, lava: true },
            { x: 0.3, y: 0.6, radius: 60 },
            { x: 0.7, y: 0.74, radius: 56 },
          ],
          turrets: [{ x: 0.5, y: 0.88 }],
        },
        {
          exit: 'right', len: 2300, span: 1400, entryAt: 0.2,
          obstacles: [{ x: 0.55, y: 0.7, radius: 64 }, { x: 0.74, y: 0.26, radius: 72, lava: true }],
        },
      ],
      chamber: { obstacles: [{ x: 0.82, y: 0.2, radius: 62 }, { x: 0.82, y: 0.8, radius: 62 }] },
    },
  },
];

/** Every route — APPEND new ones at the end (saves index LAYOUTS). */
export const ROUTES: ReadonlyArray<ArenaLayout> = [...ROUTES_SHALLOW, ...ROUTES_DEEP];
